import Link from 'next/link'
import { isNoindexLocality } from 'utils/sitemap-config'
import Head from 'next/head'
import { downloadServiceProviders } from '../../../../../api/service-provider/sp-list'
import { withDevRosterFallback } from 'utils/dev-roster-fallback'
import { countryList, distanceUnitArray, s3Static } from '../../../../../utils/constants'
import Script from 'next/script'
import { contentDate } from '../../../../../utils/article-date-time'
import { Locality } from '../../../../../components/index/commons/Locality'
import { Inter } from 'next/font/google';
import { getRequestJSON } from 'api/index/location-api'
import { useEffect, useState } from 'react'
import { openModal } from 'service/modal-service'
import { useRouter } from 'next/router'
import { getTestimonials } from 'api/user/profile-api'
import { getPtTestimonials, setPtTestimonials } from 'service/testimonial-store'
import { TopTrainersSection } from '@/index/service-category/personal-trainer/TopTrainersSection'
import { HERO_TRAINER_COUNT } from '@/index/service-category/personal-trainer/HeroTrainerStrip'
import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerHead } from '@/index/service-category/personal-trainer/PersonalTrainerHead'
import { PersonalTrainerWhyUs } from '@/index/service-category/personal-trainer/PersonalTrainerWhyUs'
import { PersonalTrainerBooking } from '@/index/service-category/personal-trainer/PersonalTrainerBooking'
import { TrainerCustomerReviews } from '@/index/service-category/personal-trainer/TrainerCustomerReviews'
import { toTrainerCards } from 'utils/trainer-card-projection'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-11'
const inter = Inter({ subsets: ['latin'], display: 'swap' });
// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps({params}) {
    const locality = params.locality

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);

    const countryCd = "sg"
    const countryName = "Singapore";
    const currencyCd = "SGD";
    let localityDetailsList;
    let localityDetails = {"name": "", "description": ""};
    let lat = 1.3521
    let lng = 103.8198

    const allRequestCategorySettings = await getRequestJSON(countryName)
    const testimonials = getPtTestimonials().length > 0 ? getPtTestimonials() : await getTestimonials("Fitness Trainer", 0, 6)
    setPtTestimonials(testimonials)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

    const getCountryDetails = () => {
        for(let x in countryList){
            if(countryCd === countryList[x].country_cd){
                localityDetailsList = countryList[x].localityListDetails
                return
            }
        }
    }

    getCountryDetails()

    const getLocalityDetails = () => {
        for(let x in localityDetailsList){
            if(locality === localityDetailsList[x].name){
                localityDetails = localityDetailsList[x]
                lat = localityDetails.lat
                lng = localityDetails.lng
                return
            }
        }
    } 
    
    getLocalityDetails()

    const getDistanceMetrics = () => {
      for(let x in distanceUnitArray){
        if(countryName === distanceUnitArray[x].country){
            return distanceUnitArray[x].unit
        }
      }
    }

    const distanceUnit = getDistanceMetrics()    

    let isOrderDistance = true
    if(locality === countryName){
        isOrderDistance = false
    }
    let radius = 10;
    // The island-wide page is the directory the hero's "See all N trainers" link
    // points at, so it fetches and lists far more than a neighbourhood page.
    let fetchSize = 30;
    if(locality === "Singapore"){
      radius = 50;
      fetchSize = 60;
    }

    const spListRaw =  await downloadServiceProviders("Fitness Trainer", 0, fetchSize, countryName, lat, lng, radius, distanceUnit, isOrderDistance, false, true)
    // Pass-through in a production build - see utils/dev-roster-fallback.
    const spList = withDevRosterFallback(spListRaw, `trainer-list ${locality}`)
    
    return {
        props: {
          countryName,
          locality,
          localityDetails,
          spList: toTrainerCards(spList),
          modifiedDate,
          month,
          year,
          requestCategorySettings,
          testimonials
        },
        // Next.js will attempt to re-generate the page:
        // - When a request comes in
        // - At most once every 3600 seconds
        revalidate: 259200, // In seconds
    }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {
    // Singapore localities only. This used to flatten EVERY country's
    // localityList, which built /sg/personal-trainer/trainer-list/Mumbai,
    // /New York etc. - Singapore pages querying Singapore trainers under a
    // foreign city name - and would have done the same for Dubai and Kuala
    // Lumpur. The old non-SG URLs 301 to the hub (next.config.js).
    // "Singapore" is excluded: island-wide is the hub's job, and this page
    // duplicated it. The URL 301s to the hub (next.config.js).
    const paths = (countryList.find((country) => country.country_cd === 'sg')?.localityList ?? [])
      .filter((locality) => locality !== 'Singapore')
      .map((locality) => ({
        params: { locality: locality },
      }))

    // We'll pre-render only these paths at build time.
    // fallback: false means anything outside localityList is a hard 404, NOT an
    // empty page - so any surface linking here must resolve to a name in this
    // list first. See getLinkableLocality in utils/trainer-locality.
    return { paths, fallback: false }
}

// Upper bound on trainers listed per page. The island-wide page acts as the
// directory for the whole roster, so it lists more than a neighbourhood page.
const LOCALITY_TRAINER_LIMIT = 18
const SINGAPORE_TRAINER_LIMIT = 48

export default function Home(props) {
    const router = useRouter()
    const locality = props.locality
    const countryName = props.countryName
    const spList = props.spList
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const localityDetails = props.localityDetails
    const requestCategorySettings = props.requestCategorySettings
    const testimonials = props.testimonials

    // Title, headings, social tags and Article schema all read this one value.
    // Previously the page claimed "Top 18" in the title and "Top 14" in og/twitter
    // and schema, while rendering 18 - and a locality with only 6 nearby trainers
    // still advertised 18. Deriving it from the rendered slice fixes both.
    const trainerLimit = locality === 'Singapore' ? SINGAPORE_TRAINER_LIMIT : LOCALITY_TRAINER_LIMIT
    const shownTrainers = spList.slice(0, trainerLimit)
    const trainerCount = shownTrainers.length
    const listingLabel = trainerCount >= 3
        ? `Top ${trainerCount} Fitness Personal Trainers`
        : 'Fitness Personal Trainers'

    const [ requestForm, setRequestForm ] = useState(false)
    
    const requestType = "Fitness Trainer"
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    useEffect(() => {
        if(router.query.requestForm === "true"){
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])

  return (
    
    <>
      <Head>
        <title>{`${listingLabel} in ${locality} [${year}]`}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description"
            content={`We bring you the top ${trainerCount} fitness personal trainers in ${locality} for ${year}. Whether you're looking to lose weight, build muscle, or improve your overall fitness, our curated list of trainers can help you achieve your goals.`} />
        <meta name="keywords"
            content="Fitness trainer Singapore, personal trainer Singapore, gym Singapore, weight loss Singapore, bodybuilding Singapore,
                    exercise Singapore, personal trainer, lifestyle Singapore, weight loss, fat loss Singapore"/>
        <meta name="author" content="StringsSG - Personal Training"/>
        {isNoindexLocality('personal-trainer', locality) && <meta name="robots" content="noindex, follow"/>}
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={`https://www.stringssg.com/sg/personal-trainer/trainer-list/${encodeURIComponent(locality)}`} />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`${listingLabel} in ${locality} [${year}]`}/>
        <meta property="og:description" content={`We bring you the top ${trainerCount} fitness personal trainers in ${locality} for ${year}. Whether you're looking to lose weight, build muscle, or improve your overall fitness, our curated list of trainers can help you achieve your goals.`} />
        <meta property="og:site_name" content={`${listingLabel} in ${locality} [${year}]`} />        
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-21T07:30:46+00:00" />
        <meta property="article:modified_time" content={`${modifiedDate}`} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/personal-trainer/trainer-list/${encodeURIComponent(locality)}`}/>
        <meta name="twitter:title" content={`${listingLabel} in ${locality} [${year}]`}/> 
        <meta name="twitter:description" content={`We bring you the top ${trainerCount} fitness personal trainers in ${locality} for ${year}. Whether you're looking to lose weight, build muscle, or improve your overall fitness, our curated list of trainers can help you achieve your goals.`}/> 
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer.jpg" /><link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@type":"Article","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/trainer-list/${encodeURIComponent(locality)}"},"headline":"${listingLabel} in ${locality} ${month} ${year}","description":"We bring you the top ${trainerCount} fitness personal trainers in ${locality} for ${year}. Whether you're looking to lose weight, build muscle, or improve your overall fitness, our curated list of trainers can help you achieve your goals.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-26T07:30:46+00:00","dateModified":"${modifiedDate}","articleSection":["Fitness","Wellness"]},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Personal Trainer","item":"https://www.stringssg.com/sg/personal-trainer"},{"@type":"ListItem","position":2,"name":"List","item":"https://www.stringssg.com/sg/personal-trainer/trainer-list/${encodeURIComponent(locality)}"},{"@type":"ListItem","position":3,"name":"${locality}"}]}]}`
          }}
        />
        <link rel="canonical" href={`https://www.stringssg.com/sg/personal-trainer/trainer-list/${encodeURIComponent(locality)}`} />
      </Head> 
        <div className={`${inter.className} bg-slate-900 `}>
            <RequestProvider>
                <PersonalTrainerHead 
                    heroImage={s3Static + "personal-trainer/personal-trainer.jpg"}
                    title={`${listingLabel} in ${locality}`}
                    heading={`${listingLabel} in ${locality}`}
                    heroCopy={`Certified personal trainers serving ${locality} from $84/session. Train at home, your condo gym or a nearby park — no gym membership, no lock-in.`}
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                    spList={shownTrainers}
                    trainerCount={trainerCount}
                    seeAllHref="/sg/personal-trainer#trainer-map"
                    seeAllLabel="See personal trainers across Singapore"
                />
                <div className='relative py-10 max-w-7xl mx-auto px-3'>
                    <div className="mb-6">
                        {/* <h2 className='text-center text-2xl md:text-4xl font-extrabold text-white tracking-tight'>
                            Top {trainerCount} Fitness Personal Trainers in {locality}
                        </h2> */}
                        <p className="max-w-5xl mx-auto wrap-break-word whitespace-pre-wrap text-lg leading-relaxed space-y-2 py-10 text-white/70">
                            {localityDetails.services?.personal_trainer}
                        </p>
                        <p className="max-w-5xl mx-auto text-base leading-relaxed text-white/60">
                            Working out a budget before you enquire? Our guide to{' '}
                            <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false} className="text-orange-400/90 hover:text-orange-400 underline">
                                how much a personal trainer costs in Singapore
                            </Link>{' '}
                            breaks down what trainers charge per session across {locality} and the rest of the island, by experience level, training location and
                            package size.
                        </p>
                    </div>
                    <div className="mx-auto w-fit flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                        <span className="text-sm">🏆</span>
                        <span className="text-xs font-medium text-white/70">Top Trainers</span>
                    </div>
                    {/* No grid here: TopTrainersSection owns its own responsive grid. Nesting it
                        inside another one put the whole list in a single cell, so on desktop
                        it rendered a third of the width. */}
                    <div>                    
                        <TopTrainersSection spList={shownTrainers.slice(HERO_TRAINER_COUNT)}/>     
                    </div>
                </div> 
                <PersonalTrainerWhyUs/>     
                <PersonalTrainerBooking setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} year={year} /> 
                <TrainerCustomerReviews testimonials={testimonials} />
            </RequestProvider>
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
